import Icons from '@/const/icons';
import { Button } from '../components/common/button';
import { Typography } from '../components/common/typography';
import { Transition, Dialog } from '@headlessui/react';
import clsx, { ClassValue } from 'clsx';
import React, { Fragment, useCallback, useState } from 'react';
// import { useMediaQuery } from 'react-responsive';

interface ModalPreviewPageProps {
	centerText?: boolean;
	handleClickEdit?: () => void;
	handleClickBack: () => void;
	handleClickApprove?: () => void;
	className?: ClassValue;
	classNameHeader?: ClassValue;
	classNameCardContainer?: ClassValue;
	classNameContainerChildren?: ClassValue;
	children?: React.ReactNode;
	title?: string;
	hideEditButton?: boolean;
	hideApproveButton?: boolean;
	hideTitle?: boolean;
	ariaLabel?: string;
	backCustomLabel?: string;
	approveCustomLabel?: string;
	onClose?: () => void;
}
/**
 * Custom hook to return a modal with its handler functions,
 * the returned modal has a default layout
 *
 * @returns
 * 		Modal: Default Modal element that will be rendered
 * 		isOpen: usual modal prop that says when the modal should appear
 * 		show: Handler function to show the modal
 * 		hide: Handler function to hide the modal
 */

const useModalPreviewPage = (): {
	ModalPreviewPage: React.FC<ModalPreviewPageProps>;
	isOpen: boolean;
	showPreviewPage: () => void;
	hidePreviewPage: () => void;
} => {
	const cancelButtonRef = React.useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	//this was unused

	// const isDesktopOrLaptop = useMediaQuery({
	// 	query: '(min-width: 1024px)',
	// });

	const showPreviewPage = (): void => {
		setIsOpen(true);
	};

	const hidePreviewPage = (): void => {
		setIsOpen(false);
	};

	const ModalPreviewPage: React.FC<ModalPreviewPageProps> = useCallback(
		(props) => (
			<Transition.Root appear show={isOpen} as={Fragment}>
				<Dialog
					as="div"
					className="relative"
					onClose={() => {
						hidePreviewPage();
						props.onClose?.();
					}}
					static
					initialFocus={cancelButtonRef}
				>
					<div
						className={clsx('z-30 fixed top-0 w-full h-full', props.className)}
					>
						<div className="flex h-full w-full relative">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<Dialog.Panel
									aria-label={props.ariaLabel}
									className={clsx(
										'transform transition-all w-full h-full bg-white ',
										props.classNameCardContainer
									)}
								>
									{/* bg-white py-8 !px-20 flex items-center justify-between gap-x-2 */}
									<div
										className={clsx(
											'bg-white py-0 md:py-6 px-6 md:!px-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-x-2',
											'relative',
											props.classNameHeader
										)}
									>
										<Button
											label={props.backCustomLabel || 'Back'}
											size="fit"
											icon={Icons.arrowLeft}
											iconLeft
											className="border-none !w-auto !pl-0 md:pl-6"
											classNameLabel="text-lg md:text-base"
											onClick={props.handleClickBack}
										/>
										{!props.hideTitle && (
											<Typography
												style={
													props.centerText &&
													props.hideEditButton &&
													props.hideApproveButton
														? { paddingRight: '210px' }
														: { paddingLeft: '23px' }
												}
												type="semibold-16"
												className="text-opacity-black text-center lg:absolute content-center left-0 right-0 m-auto max-w-max"
											>
												{props.title ||
													`This is your preview page. Select 'Edit' to make changes or \n'Approve' to stage it for going live.`}
											</Typography>
										)}
										<div className="flex flex-col md:flex-row items-center gap-x-6 w-full md:w-auto space-y-3 md:space-y-0 mt-3 md:mt-0">
											{!props.hideEditButton && (
												<Button
													label="Edit"
													size="medium"
													decoration="secondary"
													onClick={props.handleClickEdit}
													className="3xl:py-[14px]"
												/>
											)}
											{!props.hideApproveButton && (
												<Button
													label={props.approveCustomLabel || 'Approve'}
													size="medium"
													decoration="primary"
													onClick={props.handleClickApprove}
													className="3xl:py-[14px]"
												/>
											)}
										</div>
									</div>
									<div
										className={clsx(
											'overflow-auto scroll-custom h-full mb-10 pb-12 relative min-h-screen',
											props.classNameContainerChildren
										)}
									>
										{props.children}
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
		),
		[isOpen]
	);

	return {
		ModalPreviewPage,
		isOpen,
		showPreviewPage,
		hidePreviewPage,
	};
};

export default useModalPreviewPage;
